import React, { useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, LinearProgress, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { Pill, SectionCard, ActionIconBtn, helperProps } from '../components/common'
import DataTable from '../components/Tables'
import PricingRailsCard from '../components/PricingRailsCard'
import {
  useGetRestrictedCouriersQuery, useAddRestrictedCourierMutation, useRemoveRestrictedCourierMutation,
  useGetSupplyCommercialsQuery, useGetDropServiceabilityQuery, useImportDropServiceabilityMutation,
  useGetCpServiceMappingQuery, useGetCpCapabilityQuery, useUpdateCpCapabilityMutation,
  useGetCouriersQuery,
} from '../../../redux/d2cApiSlice'

const yn = (v) => (v === true ? '✓' : v === false ? '✗' : '—')

export default function FHMasters() {
  const { enqueueSnackbar } = useSnackbar()

  // §2 Restricted couriers
  const { data: restricted = [] } = useGetRestrictedCouriersQuery()
  const { data: couriers = [] } = useGetCouriersQuery()
  const [addRestricted] = useAddRestrictedCourierMutation()
  const [removeRestricted] = useRemoveRestrictedCourierMutation()
  const [addCode, setAddCode] = useState('')
  const [addCodeErr, setAddCodeErr] = useState(false) // reveal "courier required" after a failed Restrict click

  // §3-5 read-only masters
  const { data: supply, isLoading: supplyLoading } = useGetSupplyCommercialsQuery({})
  const { data: dropData, isLoading: dropLoading } = useGetDropServiceabilityQuery({ page: 0, size: 200 })
  const { data: cpMap = [], isLoading: cpMapLoading } = useGetCpServiceMappingQuery({})
  const [importDrop, importState] = useImportDropServiceabilityMutation()

  // §6 CP capability
  const { data: cpCap = [], isLoading: capLoading } = useGetCpCapabilityQuery()
  const [updateCap, updateCapState] = useUpdateCpCapabilityMutation()
  const [edit, setEdit] = useState(null) // { courierId, courier, d2cEnabled, codSupported, prepaidSupported, maxWeightKg, notes }

  const supplyRows = supply?.rows || []
  const dropRows = dropData?.content || dropData?.rows || (Array.isArray(dropData) ? dropData : [])

  const doAddRestricted = async () => {
    if (!addCode) { setAddCodeErr(true); return }
    try { await addRestricted(addCode).unwrap(); setAddCode(''); setAddCodeErr(false); enqueueSnackbar('Courier restricted.', { variant: 'success' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Failed (FH only).', { variant: 'error' }) }
  }
  const doRemoveRestricted = async (code) => {
    try { await removeRestricted(code).unwrap(); enqueueSnackbar('Restriction removed.', { variant: 'info' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Failed (FH only).', { variant: 'error' }) }
  }
  const doImportDrop = async (file) => {
    if (!file) return
    const fd = new FormData(); fd.append('file', file)
    try { const res = await importDrop(fd).unwrap(); enqueueSnackbar(`Import complete — ${res?.recordsProcessed ?? 0} records.`, { variant: 'success' }) }
    catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Import failed.', { variant: 'error' }) }
  }
  const saveCap = async () => {
    try {
      await updateCap({ courierId: edit.courierId, body: { d2cEnabled: edit.d2cEnabled, codSupported: edit.codSupported, prepaidSupported: edit.prepaidSupported, maxWeightKg: Number(edit.maxWeightKg) || 0, notes: edit.notes || '' } }).unwrap()
      enqueueSnackbar('Capability updated.', { variant: 'success' })
      setEdit(null)
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Update failed.', { variant: 'error' }) }
  }

  const supplyCols = [
    { key: 'courier', label: 'Courier', render: (r) => <b>{r.courier}</b> },
    { key: 'serviceType', label: 'Service' },
    { key: 'zone', label: 'Zone' },
    { key: 'weightSlab', label: 'Weight slab' },
    { key: 'rate', label: 'Rate ₹', align: 'right' },
    { key: 'codFlat', label: 'COD ₹', align: 'right' },
    { key: 'rtoPct', label: 'RTO %', align: 'right' },
    { key: 'tat', label: 'TAT', align: 'right', render: (r) => `${r.minTat ?? '—'}–${r.maxTat ?? '—'}` },
  ]
  const dropCols = [
    { key: 'pincode', label: 'Pincode', render: (r) => <b>{r.pincode}</b> },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'isServiceable', label: 'Serviceable', align: 'center', render: (r) => yn(r.isServiceable) },
    { key: 'tat', label: 'TAT', align: 'right', render: (r) => `${r.minTat ?? '—'}–${r.maxTat ?? '—'}` },
  ]
  const cpMapCols = [
    { key: 'courier', label: 'Courier', render: (r) => <b>{r.courier}</b> },
    { key: 'serviceType', label: 'Service' },
    { key: 'codSupported', label: 'COD', align: 'center', render: (r) => yn(r.codSupported) },
    { key: 'prepaidSupported', label: 'Prepaid', align: 'center', render: (r) => yn(r.prepaidSupported) },
    { key: 'status', label: 'Status', render: (r) => <Pill label={r.status || '—'} styleKey={r.status === 'ACTIVE' ? 'approved' : 'neutral'} /> },
    { key: 'maxWt', label: 'D2C max wt', align: 'right', render: (r) => (r.d2cCapability?.maxWeightKg ?? '—') },
  ]
  const capCols = [
    { key: 'courier', label: 'Courier', render: (r) => <b>{r.courier}</b> },
    { key: 'serviceType', label: 'Service' },
    { key: 'd2cEnabled', label: 'D2C enabled', align: 'center', render: (r) => (r.capability ? yn(r.capability.d2cEnabled) : '— (default on)') },
    { key: 'maxWeightKg', label: 'Max wt (kg)', align: 'right', render: (r) => (r.capability?.maxWeightKg ?? '—') },
    { key: 'notes', label: 'Notes', render: (r) => r.capability?.notes || '—' },
    {
      key: 'a', label: 'Action', align: 'right', render: (r) => (
        <ActionIconBtn title="Edit"
          onClick={() => setEdit({ courierId: r.courierId, courier: r.courier, d2cEnabled: r.capability?.d2cEnabled ?? true, codSupported: r.capability?.codSupported ?? true, prepaidSupported: r.capability?.prepaidSupported ?? true, maxWeightKg: r.capability?.maxWeightKg ?? '', notes: r.capability?.notes ?? '' })}>
          <EditOutlinedIcon fontSize="small" />
        </ActionIconBtn>
      ),
    },
  ]

  // cpAccountCodes not already restricted, for the add dropdown.
  const addable = couriers.filter((c) => !restricted.includes(c.cpAccountCode))

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>Supply Masters &amp; CP Capability</PageTitle>

      <PricingRailsCard />

      <SectionCard title="Restricted Couriers" subtitle="Restricted couriers still price, but any deal using one is forced to Functional-Head review (L1 can’t self-approve).">
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField select required size="small" label="Add courier" value={addCode} onChange={(e) => { setAddCodeErr(false); setAddCode(e.target.value) }} sx={{ minWidth: 260 }}
            error={addCodeErr && !addCode} {...helperProps(addCodeErr && !addCode ? 'Pick a courier to restrict.' : undefined)}>
            {addable.map((c) => <MenuItem key={c.cpAccountCode} value={c.cpAccountCode}>{c.name} ({c.cpAccountCode})</MenuItem>)}
          </TextField>
          <Button variant="contained" size="small" sx={{ textTransform: 'none', borderRadius: '6px' }} onClick={doAddRestricted}>Restrict</Button>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {restricted.length === 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No restricted couriers.</Typography>}
          {restricted.map((code) => <Chip key={code} label={code} onDelete={() => doRemoveRestricted(code)} sx={{ fontWeight: 600 }} />)}
        </Stack>
      </SectionCard>

      <SectionCard title="Supply Commercials" subtitle="Read-only — supply rates come from courier partner SLAs. Update via TMS courier management.">
        {supplyLoading && <LinearProgress sx={{ my: 1 }} />}
        <DataTable columns={supplyCols} rows={supplyRows} getRowKey={(r, i) => `${r.cpAccountCode}-${r.serviceType}-${r.zone}-${r.weightSlab}-${i}`} emptyText={supplyLoading ? 'Loading…' : 'No rows'} />
      </SectionCard>

      <SectionCard
        title="Drop Serviceability"
        subtitle="Pincode serviceability + TAT. Import upserts the pincode master."
        action={
          <Button size="small" variant="outlined" component="label" sx={{ textTransform: 'none', borderRadius: '6px' }} disabled={importState.isLoading}>
            {importState.isLoading ? 'Importing…' : 'Import .xlsx'}
            <input type="file" hidden accept=".xlsx" onChange={(e) => doImportDrop(e.target.files?.[0])} />
          </Button>
        }
      >
        {dropLoading && <LinearProgress sx={{ my: 1 }} />}
        <DataTable columns={dropCols} rows={dropRows} getRowKey={(r) => r.id || r.pincode} emptyText={dropLoading ? 'Loading…' : 'No rows'} />
      </SectionCard>

      <SectionCard title="CP ↔ Service Mapping" subtitle="Read-only — from courier partners.">
        {cpMapLoading && <LinearProgress sx={{ my: 1 }} />}
        <DataTable columns={cpMapCols} rows={cpMap} getRowKey={(r, i) => `${r.cpAccountCode}-${r.serviceType}-${i}`} emptyText={cpMapLoading ? 'Loading…' : 'No rows'} />
      </SectionCard>

      <SectionCard title="CP Capability" subtitle="D2C-specific per-courier overrides. A courier disabled here is excluded from the pricing engine.">
        {capLoading && <LinearProgress sx={{ my: 1 }} />}
        <DataTable columns={capCols} rows={cpCap} getRowKey={(r, i) => `${r.courierId}-${r.serviceType}-${i}`} emptyText={capLoading ? 'Loading…' : 'No couriers'} />
      </SectionCard>

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>CP Capability — {edit?.courier}</DialogTitle>
        <DialogContent dividers>
          {edit && (
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12}><FormControlLabel control={<Switch checked={!!edit.d2cEnabled} onChange={(e) => setEdit((s) => ({ ...s, d2cEnabled: e.target.checked }))} />} label="D2C enabled (off = excluded from pricing)" /></Grid>
              <Grid item xs={6}><FormControlLabel control={<Switch checked={!!edit.codSupported} onChange={(e) => setEdit((s) => ({ ...s, codSupported: e.target.checked }))} />} label="COD" /></Grid>
              <Grid item xs={6}><FormControlLabel control={<Switch checked={!!edit.prepaidSupported} onChange={(e) => setEdit((s) => ({ ...s, prepaidSupported: e.target.checked }))} />} label="Prepaid" /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Max weight (kg)" value={edit.maxWeightKg} onChange={(e) => setEdit((s) => ({ ...s, maxWeightKg: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={edit.notes} onChange={(e) => setEdit((s) => ({ ...s, notes: e.target.value }))} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEdit(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" disabled={updateCapState.isLoading} onClick={saveCap} sx={{ textTransform: 'none' }}>{updateCapState.isLoading ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
